/*
 * Copyright (c) 2022 automata-network/tgb-donation authors and contributors.
 * This software may be modified and distributed under the terms of MIT license.
 * Refer to the LICENSE file for details.
 */
import { useEffect, useState } from 'react'
import styled, { css } from 'styled-components'

import { pxToEm, pxToVw } from '../../../functions/units'
import { PrimaryButton } from '../../Button'
import FlexBox from '../../Common/FlexBox'

type Props = {
  onGuideConfirmed?: () => void
}

const CryptoGuide = ({ onGuideConfirmed }: Props) => {
  const [confirmationChecked, setConfirmationChecked] = useState<boolean>(false)
  const [firstTimeGuide, setFirstTimeGuide] = useState<boolean>(true)

  useEffect(() => {
    const localStorageCryptoGuideDisabled = localStorage.getItem('cryptoGuideDisabled')
    if (localStorageCryptoGuideDisabled && Number(localStorageCryptoGuideDisabled) === 1) {
      setFirstTimeGuide(false)
    }
  }, [])

  return (
    <StyledContainer>
      <StyledArticle>
        {/*<img src={illustrationResult} alt="" />*/}

        <h1>Crypto Payment User Guide</h1>
        <p>
          Thank you for your intention to donate on our platform. Please read this guide before you
          proceed to the crypto payment details to avoid problems during the transaction.
        </p>

        <h2>Summary</h2>
        <p>
          We use Triple A as a third party in processing all crypto payments we receive. The Triple
          A payment system currently supports the following cryptocurrencies:
        </p>
        <ul>
          <li>Bitcoin (BTC),</li>
          <li>Ethereum (ETH),</li>
          <li>USDC (ERC-20), and</li>
          <li>USDT (ERC-20)</li>
        </ul>
        <p>
          ERC-20 tokens must <strong>only</strong> be transferred on the{' '}
          <strong>Ethereum network</strong>. Sending ERC-20 tokens on any other networks may result
          in losing your funds.
        </p>

        <h2>Supported Wallets</h2>
        <p>
          You may transfer crypto funds by scanning on the QR code or manually inserting the address
          on your wallet.
        </p>
        <p>
          If you are having issues with the QR code, you may click on the link to simplify QR code.
        </p>
        <p>
          <img
            src="/images/guide-simplified-qr.png"
            alt="How to simplified crypto payment QR code"
          />
        </p>

        <p>We currently recommend using the following wallets:</p>
        <div>
          <details>
            <summary>Trust Wallet</summary>
            <ul>
              <li>✅&ensp;Transfer of BTC</li>
              <li>✅&ensp;Transfer of ETH and ERC-20 tokens</li>
            </ul>
          </details>

          <details>
            <summary>Coinhako</summary>
            <ul>
              <li>✅&ensp;Transfer of BTC</li>
              <li>✅&ensp;Transfer of ETH and ERC-20 tokens</li>
            </ul>
          </details>

          <details>
            <summary>MetaMask</summary>
            <ul>
              <li>✅&ensp;Transfer of ETH and ERC-20 tokens</li>
              <li>❌&ensp;Transfer of BTC</li>
            </ul>
          </details>

          <details>
            <summary>Coinomi</summary>
            <ul>
              <li>✅&ensp;Transfer of BTC</li>
              <li>✅&ensp;Transfer of ETH and ERC-20 tokens</li>
            </ul>
          </details>

          <details>
            <summary>Coinbase Wallet (using simplified QR code)</summary>
            <ul>
              <li>✅&ensp;Transfer of BTC</li>
              <li>✅&ensp;Transfer of ETH and ERC-20 tokens</li>
            </ul>
          </details>
        </div>

        <p>
          Or, you may otherwise send your crypto donations directly from centralized exchanges
          listed below:
        </p>
        <div>
          <details>
            <summary>Coinbase (using simplified QR code)</summary>
            <ul>
              <li>✅&ensp;Transfer of BTC</li>
              <li>✅&ensp;Transfer of ETH and ERC-20 tokens</li>
            </ul>
          </details>

          <details>
            <summary>Binance (using simplified QR code)</summary>
            <ul>
              <li>✅&ensp;Transfer of BTC</li>
              <li>✅&ensp;Transfer of ETH and ERC-20 tokens</li>
            </ul>
          </details>

          <details>
            <summary>Crypto.com (using simplified QR code)</summary>
            <ul>
              <li>✅&ensp;Transfer of BTC</li>
              <li>✅&ensp;Transfer of ETH and ERC-20 tokens</li>
            </ul>
          </details>

          <details>
            <summary>Luno</summary>
            <ul>
              <li>✅&ensp;Transfer of BTC</li>
              <li>✅&ensp;Transfer of ETH</li>
              <li>⚠️&ensp;Transfer of ERC-20 tokens (might be prohibited in some regions)</li>
            </ul>
          </details>

          <details>
            <summary>Gemini</summary>
            <ul>
              <li>✅&ensp;Transfer of BTC</li>
              <li>✅&ensp;Transfer of ETH</li>
              <li>⚠️&ensp;Transfer of ERC-20 tokens (only USDC)</li>
            </ul>
          </details>
        </div>

        <h2>Guaranteed Rate</h2>
        <p>
          The Triple A payment system guarantees you to get the same exchange rate in a 25-minutes
          window. You must complete your payment within that time or the payment details will be
          considered expired.
        </p>
        <p>
          If you are unable to make a payment during the guaranteed exchange rate time and you get
          the <code>Payment Form has expired</code> message, simply back to the home page and create a
          new payment request using our donation form.
        </p>

        {firstTimeGuide && (
          <>
            <hr />

            <StyledConfirmationWrap flexDirection="column" rowGap={16}>
              <FlexBox as='label' alignItems='center' columnGap={4}>
                <input type="checkbox" onChange={(e) => setConfirmationChecked(e.currentTarget.checked)} />I have read the guide and want to make a payment now
              </FlexBox>

              <PrimaryButton disabled={!confirmationChecked} size="large" onClick={onGuideConfirmed}>
                Proceed to Crypto Payment Details
              </PrimaryButton>
            </StyledConfirmationWrap>
          </>
        )}
      </StyledArticle>
    </StyledContainer>
  )
}

export default CryptoGuide

const StyledContainer = styled.div`
  padding-inline: ${pxToVw(410)};
  padding-top: ${pxToVw(120)};

  ${({ theme }) => css`
    ${theme.mediaQuery.mobile`
      padding-inline: ${pxToVw(60, 720)};
      padding-top: 0;
    `}
  `}
`

const StyledArticle = styled.article`
  margin-bottom: ${pxToVw(155)};

  > h1 {
    color: ${({ theme }) => theme.colors.gray700};
    font-size: ${pxToVw(50)};
    font-weight: 700;
    margin-bottom: ${pxToVw(34)};
  }

  > h2 {
    color: ${({ theme }) => theme.colors.gray700};
    font-size: ${({ theme }) => pxToVw(theme.units.fontSize.header2)};
    font-weight: 500;
    margin-bottom: ${pxToVw(20)};
  }

  > p {
    color: ${({ theme }) => theme.colors.gray500};
    font-size: ${pxToVw(26)};
    line-height: ${pxToEm(34, 26)};

    &:not(:last-child) {
      margin-bottom: ${pxToVw(34)};
    }

    > strong {
      color: ${({ theme }) => theme.colors.gray600};
      font-weight: 500;
    }

    > img {
      display: flex;
      margin-inline: auto;
      max-height: 256px;
      object-fit: contain;
    }

    > code {
      background-color: ${({ theme }) => theme.colors.gray700};
      color: ${({ theme }) => theme.colors.gray200};
      padding: ${pxToVw(2)} ${pxToVw(7)};
      border-radius: ${pxToVw(7.5)};
    }
  }

  > ul {
    color: ${({ theme }) => theme.colors.gray500};
    font-size: ${pxToVw(26)};
    line-height: ${pxToEm(34, 26)};
  }

  details {
    border: solid 1px ${({ theme }) => theme.colors.gray300};
    padding: ${pxToVw(12)} ${pxToVw(20)};

    &:not(:last-child) {
      border-bottom: 0;
    }
    &:first-child {
      border-radius: ${pxToVw(10)} ${pxToVw(10)} 0 0;
    }
    &:last-child {
      border-radius: 0 0 ${pxToVw(10)} ${pxToVw(10)};
    }

    > summary {
      color: ${({ theme }) => theme.colors.gray600};
      font-weight: 500;
      cursor: pointer;
    }

    > ul {
      list-style: none;
      padding-left: ${pxToVw(24)};

      > li {
        color: ${({ theme }) => theme.colors.gray600};
      }
    }
  }

  ${({ theme }) => css`
    ${theme.mediaQuery.mobile`
      margin-bottom: ${pxToVw(155, 720)};

      > h1 {
        font-size: ${pxToVw(50, 720)};
        margin-bottom: ${pxToVw(34, 720)};
      }

      > h2 {
        font-size: ${pxToVw(theme.units.fontSize.header2, 720)};
        margin-bottom: ${pxToVw(20, 720)};
      }

      > p {
        font-size: ${pxToVw(26, 720)};

        &:not(:last-child) {
          margin-bottom: ${pxToVw(34, 720)};
        }

        > img {
          max-height: 256px;
        }

        > code {
          padding: ${pxToVw(2, 720)} ${pxToVw(7, 720)};
          border-radius: ${pxToVw(7.5, 720)};
        }
      }

      > ul {
        font-size: ${pxToVw(26, 720)};
      }

      details {
        padding: ${pxToVw(12, 720)} ${pxToVw(20, 720)};

        &:first-child {
          border-radius: ${pxToVw(10, 720)} ${pxToVw(10, 720)} 0 0;
        }
        &:last-child {
          border-radius: 0 0 ${pxToVw(10, 720)} ${pxToVw(10, 720)};
        }

        > ul {
          padding-left: ${pxToVw(24, 720)};
        }
      }
    `}
  `}
`

const StyledConfirmationWrap = styled(FlexBox)`
  margin-top: ${pxToVw(48)};

  > button {
    width: 100%;

    &:disabled {
      opacity: 0.5;
    }
  }

  ${({ theme }) => css`
    ${theme.mediaQuery.mobile`
      margin-top: ${pxToVw(48, 720)};
    `}
  `}

`
